import {
  isModalTaskType,
  isModalTaskTypeLabel,
} from "pages/tasks/utils/task-type-utils";
import type { TaskDataType } from "types/DataTypes";
import { logToSentry } from "utils/sentry";

export type RushType = "24-hour" | "3-day" | "rush" | null;

type ValidationCounts = {
  errorCount: number;
  warningCount: number;
};

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i;

const sanitizeTaskMessage = (message: string): string => {
  const trimmedMessage = message.trim();

  if (!HTML_TAG_PATTERN.test(trimmedMessage)) {
    return trimmedMessage;
  }

  if (typeof DOMParser === "undefined") {
    return trimmedMessage;
  }

  const parsedDocument = new DOMParser().parseFromString(
    trimmedMessage,
    "text/html",
  );
  return (parsedDocument.body.textContent ?? "").trim();
};

const extractTextFromRichContent = (content: unknown): string => {
  if (Array.isArray(content)) {
    return content.map(extractTextFromRichContent).join(" ");
  }

  if (!content || typeof content !== "object") {
    return "";
  }

  if ("type" in content && content.type === "text") {
    return "text" in content && typeof content.text === "string"
      ? content.text
      : "";
  }

  if ("content" in content) {
    return extractTextFromRichContent(content.content);
  }

  return "";
};

/**
 * task-item helpers keep rendering code focused on structure.
 */
export const shouldOpenTaskModal = (task: TaskDataType): boolean =>
  isModalTaskType(task.type) || isModalTaskTypeLabel(task.type_verbose);

export const getRushType = (task: TaskDataType): RushType => {
  if (task.is_24_hour_rush) return "24-hour";
  if (task.is_3_day_rush) return "3-day";
  if (task.is_rush) return "rush";
  return null;
};

export const isOpsTask = (task: TaskDataType): boolean =>
  !task.claim_numbers?.length && !task.client_name && !task.file;

export const formatClaimNumbers = (
  claimNumbers: string | string[] | null | undefined,
): string => {
  if (
    !claimNumbers ||
    (Array.isArray(claimNumbers) && claimNumbers.length === 0)
  ) {
    return "No claim number";
  }

  const numbers = (
    Array.isArray(claimNumbers) ? claimNumbers : claimNumbers.split(",")
  )
    .map((claimNumber) => claimNumber.trim())
    .filter(Boolean);

  if (numbers.length === 0) return "No claim number";

  if (numbers.length === 1) {
    return `CLAIM NUMBER: ${numbers[0]}`;
  }

  return numbers
    .map((number, index) =>
      index === 0 ? `CLAIM NUMBERS: ${number}` : `${number}`,
    )
    .join("\n");
};

export const getValidationCounts = (
  validations: TaskDataType["validations"],
): ValidationCounts => {
  if (!validations?.errors || !Array.isArray(validations.errors)) {
    return { errorCount: 0, warningCount: 0 };
  }

  const counts: ValidationCounts = { errorCount: 0, warningCount: 0 };

  for (const validation of validations.errors) {
    if (validation.level === "ERROR") counts.errorCount += 1;
    if (validation.level === "WARN") counts.warningCount += 1;
  }

  return counts;
};

export const getGenericTaskMessage = (
  message: string | null,
): string | null => {
  if (!message) return message;

  try {
    const parsed = JSON.parse(message);

    if (
      parsed &&
      typeof parsed === "object" &&
      "type" in parsed &&
      parsed.type === "doc" &&
      "content" in parsed
    ) {
      return sanitizeTaskMessage(extractTextFromRichContent(parsed.content));
    }
  } catch (error) {
    logToSentry("Task swimlane item message JSON parse failed", "warn", {
      component: "kaboodleSwimlaneItemUtils",
      functionName: "getGenericTaskMessage",
      messageLength: message.length,
      errorMessage: error instanceof Error ? error.message : "unknown",
    });

    return sanitizeTaskMessage(message);
  }

  return sanitizeTaskMessage(message);
};
